import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useCdrDetail } from '../../lib/api/hooks.js';
import { useCan, useOwnOnly } from '../../lib/auth/session.js';
import { Modal } from '../../components/ui/Modal.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RecordingPlayer } from '../cdrs/RecordingPlayer.js';
import { readableNumber } from '../cdrs/CallDetail.js';
import { formatDateTime, formatDuration } from '../../lib/format.js';
import { toastError } from '../../components/ui/Toast.js';
import { DISPOSITION_GROUPS, DISPOSITION_GROUP_LABELS, type DispositionCode, type WorkspaceControls } from '@arcnet/contracts';

/**
 * One call, to work on (owner, 3 October 2026, layout B of three mock-ups: "wide split"): who it
 * was and the recording at the top; the transcript on the left, following the recording and
 * playing from any line; what the call was, its tags and its notes on the right -- during the
 * shift or any time after. Opened from the pencil on a call.
 */
interface Work {
  call_uuid: string;
  may_edit: boolean;
  disposition: { code: string; label: string; kind: 'positive' | 'neutral' | 'negative'; note: string | null; updated_at: string; by: string | null } | null;
  tags: string[];
  tag_options: string[];
  notes: Array<{ id: string; body: string; created_at: string; edited_at: string | null; author: string; mine: boolean }>;
  transcript: { state: string; lines: Array<{ seq: number; speaker: 'agent' | 'caller'; start_ms: number; end_ms: number; text: string }> } | null;
}

export function CallWorkModal({ tenantId, callId, tz, onClose, controls }: {
  tenantId: string; callId: string; tz: string; onClose: () => void;
  /** The agent's workspace controls (0153), when opened from the workspace: what it may record. */
  controls?: WorkspaceControls | undefined;
}) {
  const detail = useCdrDetail(tenantId, callId);
  const key = ['call-work', tenantId, callId];
  const work = useQuery({ queryKey: key, queryFn: () => api.get<Work>(`/v1/tenants/${tenantId}/cdrs/${callId}/work`) });
  const c = detail.data;
  const w = work.data;
  const can = useCan();
  const ownOnly = useOwnOnly();
  const mayHear = can('recording:read') || ownOnly;

  const [at, setAt] = useState(0);
  const [seekTo, setSeekTo] = useState<{ sec: number; n: number } | undefined>(undefined);
  const lines = w?.transcript?.lines ?? [];
  const current = useMemo(() => {
    const ms = at * 1000;
    let i = -1;
    for (let k = 0; k < lines.length; k++) if (lines[k]!.start_ms <= ms) i = k;
    return i;
  }, [at, lines]);

  const inbound = c?.direction === 'inbound';
  const party = c ? (inbound ? (realName(c.from_name) ?? readableNumber(c.from_number))
    : c.direction === 'internal' ? (c.to_extension_name ?? readableNumber(c.to_number)) : readableNumber(c.to_number ?? c.destination_e164)) : '';
  const partyNumber = c ? (inbound ? c.from_number : c.to_number ?? c.destination_e164) : null;
  const agent = c?.extension_name ?? (c?.extension_number ? `Extension ${c.extension_number}` : 'Agent');
  const fromName = c ? ((c.direction !== 'inbound' && c.extension_name) || realName(c.from_name) || readableNumber(c.from_number)) : '';
  const toName = c ? (c.to_extension_name ?? readableNumber(c.to_number ?? c.destination_e164)) : '';

  return (
    <Modal title="Call" size="lg" onClose={onClose}>
      <div className="arc-cw">
        {(detail.error || work.error) && <ErrorBanner error={(detail.error ?? work.error) as Error} />}
        {!c || !w ? <div className="arc-skeleton" style={{ height: 320 }} /> : (
          <>
            <div className="arc-cw-who">
              <span className="arc-call-avatar" aria-hidden="true">{initials(party)}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap' }}>
                  <strong className="arc-cw-name">{party}</strong>
                  {w.disposition && <span className="arc-pause-chip arc-disp" data-kind={w.disposition.kind} style={{ margin: 0 }}>{w.disposition.label}</span>}
                  {w.tags.map((t) => <span key={t} className="arc-chip">{t}</span>)}
                </div>
                <div className="arc-cw-meta">
                  <span><Icon name={inbound ? 'callIn' : c.direction === 'internal' ? 'callInternal' : 'callOut'} size={13} /> {inbound ? 'Inbound' : c.direction === 'internal' ? 'Internal' : 'Outbound'}</span>
                  {partyNumber && party !== readableNumber(partyNumber) && <span className="arc-mono">{readableNumber(partyNumber)}</span>}
                  <span>{agent}</span>
                  <span>{formatDateTime(c.started_at, tz)}</span>
                  <span>{c.answered_at ? formatDuration(c.duration_sec) : 'not answered'}</span>
                </div>
              </div>
            </div>

            <div className="arc-cw-split">
              <div className="arc-cw-left">
                {c.has_recording && mayHear ? (
                  <RecordingPlayer tenantId={tenantId} callId={c.id} status={c.recording_status ?? 'ready'}
                    laneNames={[fromName, toName]} expectedSha256={c.recording?.sha256 ?? null}
                    format={c.recording?.format ?? null} bytes={c.recording?.bytes ?? null}
                    onTime={setAt} seekTo={seekTo} />
                ) : (
                  <p className="arc-subtle" style={{ margin: 0, fontSize: 13 }}>
                    {c.has_recording ? 'You may not play this recording.' : 'This call was not recorded.'}
                  </p>
                )}
                <Transcript work={w} current={current} party={party} agent={agent}
                  onPlay={(ms) => setSeekTo({ sec: ms / 1000, n: Date.now() })} />
                <details className="arc-cw-details">
                  <summary>Call details</summary>
                  <dl className="arc-cw-kv">
                    <dt>Started</dt><dd>{formatDateTime(c.started_at, tz)}</dd>
                    <dt>Answered</dt><dd>{c.answered_at ? formatDateTime(c.answered_at, tz) : 'never'}</dd>
                    <dt>Talk time</dt><dd>{c.answered_at ? formatDuration(c.duration_sec) : '—'}</dd>
                    <dt>Hung up</dt><dd className="arc-mono">{c.hangup_cause ?? '—'}</dd>
                    <dt>Quality</dt><dd>{c.mos ? `MOS ${Number(c.mos).toFixed(1)}` : '—'}{c.codec ? ` · ${c.codec}` : ''}</dd>
                  </dl>
                </details>
              </div>
              <div className="arc-cw-right">
                {(controls?.outcome ?? true) && <Outcome tenantId={tenantId} callId={callId} work={w} queryKey={key} />}
                {(controls?.tags ?? true) && <Tags tenantId={tenantId} callId={callId} work={w} queryKey={key} />}
                {(controls?.notes ?? true) && <Notes tenantId={tenantId} callId={callId} work={w} queryKey={key} tz={tz} />}
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/**
 * The outcome, tags and notes of one call, on their own (owner, 3 October 2026: "a notes tab ...
 * on the call history on the main TMS"): the call window's right-hand side, for Call history's
 * own call window. Read-only for whoever may not change the call.
 */
export function CallWorkSections({ tenantId, callId, tz }: { tenantId: string; callId: string; tz: string }) {
  const key = ['call-work', tenantId, callId];
  const work = useQuery({ queryKey: key, queryFn: () => api.get<Work>(`/v1/tenants/${tenantId}/cdrs/${callId}/work`) });
  const w = work.data;
  if (work.error) return <ErrorBanner error={work.error as Error} />;
  if (!w) return <div className="arc-skeleton" style={{ height: 200 }} />;
  return (
    <div className="arc-stack" style={{ gap: 16 }}>
      <Outcome tenantId={tenantId} callId={callId} work={w} queryKey={key} />
      <Tags tenantId={tenantId} callId={callId} work={w} queryKey={key} />
      <Notes tenantId={tenantId} callId={callId} work={w} queryKey={key} tz={tz} />
    </div>
  );
}

function realName(n: string | null | undefined): string | null {
  const v = (n ?? '').trim();
  return v && !/^\+?[0-9]+$/.test(v) ? v : null;
}
function initials(who: string): string {
  const w = who.trim();
  if (!w) return '?';
  if (/^[+0-9]/.test(w)) { const d = w.replace(/\D/g, ''); return (d.length <= 4 ? d : d.slice(-2)) || '?'; }
  const words = w.split(/\s+/);
  return (words[0]![0]! + (words[1]?.[0] ?? '')).toUpperCase();
}
const clock = (ms: number) => { const s = Math.floor(ms / 1000); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };

/** The words, following the recording; a line plays from where it starts. */
function Transcript({ work, current, party, agent, onPlay }: {
  work: Work; current: number; party: string; agent: string; onPlay: (ms: number) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = box.current?.querySelector<HTMLElement>('[data-on]');
    if (el && box.current) box.current.scrollTop = el.offsetTop - box.current.offsetTop - 60;
  }, [current]);
  const t = work.transcript;
  return (
    <div>
      <p className="arc-cw-label">Transcript{t?.state === 'complete' && t.lines.length ? ' · click a line to play from there' : ''}</p>
      {!t ? <p className="arc-subtle" style={{ margin: 0, fontSize: 13 }}>Not transcribed. Auto QA transcribes the calls its rules pick, or one you ask it to.</p>
        : t.state !== 'complete' ? <p className="arc-subtle" style={{ margin: 0, fontSize: 13 }}>Being transcribed. It shows here when it is done.</p>
          : t.lines.length === 0 ? <p className="arc-subtle" style={{ margin: 0, fontSize: 13 }}>Nobody spoke on this call.</p>
            : (
              <div className="arc-cw-tx" ref={box}>
                {t.lines.map((l, i) => (
                  <button key={l.seq} type="button" className="arc-cw-line" data-on={i === current || undefined} onClick={() => onPlay(l.start_ms)}>
                    <span className="arc-cw-t arc-num">{clock(l.start_ms)}</span>
                    <span className="arc-cw-s" data-who={l.speaker}>{l.speaker === 'agent' ? agent.split(' ')[0] : /^[+0-9]/.test(party) ? 'Caller' : party.split(' ')[0]}</span>
                    <span>{l.text}</span>
                  </button>
                ))}
              </div>
            )}
    </div>
  );
}

/**
 * What the call was: the account's dispositions as buttons under their groups, each with its
 * group's colour (owner, 3 October 2026: "could look a bit more modern" than a dropdown). One press
 * marks the call; one that needs a note asks for it first.
 */
function Outcome({ tenantId, callId, work, queryKey }: { tenantId: string; callId: string; work: Work; queryKey: unknown[] }) {
  const qc = useQueryClient();
  const codes = useQuery({
    queryKey: ['disposition-codes', tenantId],
    queryFn: () => api.get<{ data: DispositionCode[] }>(`/v1/tenants/${tenantId}/disposition-codes`),
  });
  const active = (codes.data?.data ?? []).filter((d) => d.is_active);
  const [pending, setPending] = useState<DispositionCode | null>(null);
  const [note, setNote] = useState(work.disposition?.note ?? '');
  const save = useMutation({
    mutationFn: (v: { code: string; note: string | null }) => api.put(`/v1/tenants/${tenantId}/cdrs/${callId}/disposition`, v),
    onSuccess: () => { setPending(null); void qc.invalidateQueries({ queryKey }); void qc.invalidateQueries({ queryKey: ['cdrs'] }); },
    onError: toastError,
  });
  const choose = (d: DispositionCode): void => {
    if (d.requires_note) { setPending(d); return; }
    save.mutate({ code: d.code, note: null });
  };
  const current = pending?.code ?? work.disposition?.code ?? null;
  return (
    <div className="arc-field" style={{ margin: 0 }}>
      <span className="arc-label">Outcome</span>
      <div className="arc-oc">
        {DISPOSITION_GROUPS.map((g) => {
          const inGroup = active.filter((d) => d.kind === g);
          return inGroup.length ? (
            <div key={g} role="radiogroup" aria-label={DISPOSITION_GROUP_LABELS[g]}>
              <span className="arc-oc-head">{DISPOSITION_GROUP_LABELS[g]}</span>
              <div className="arc-oc-opts">
                {inGroup.map((d) => (
                  <button key={d.code} type="button" role="radio" aria-checked={current === d.code} className="arc-oc-opt"
                    data-kind={g} disabled={!work.may_edit || save.isPending} onClick={() => choose(d)}
                    title={d.requires_note ? 'Needs a note' : undefined}>
                    <span className="arc-outcome-dot" aria-hidden="true" />{d.label}
                    {current === d.code && <Icon name="check" size={13} />}
                  </button>
                ))}
              </div>
            </div>
          ) : null;
        })}
      </div>
      {pending && (
        <div className="arc-row" style={{ gap: 8, marginTop: 10 }}>
          {/* eslint-disable-next-line jsx-a11y/no-autofocus -- asked for by the press */}
          <input className="arc-input" autoFocus placeholder={`A note is needed for ${pending.label}`} value={note} maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && note.trim()) save.mutate({ code: pending.code, note: note.trim() }); }} />
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!note.trim() || save.isPending}
            onClick={() => save.mutate({ code: pending.code, note: note.trim() })}>Save</button>
          <button type="button" className="arc-btn arc-btn-sm" onClick={() => setPending(null)}>Cancel</button>
        </div>
      )}
      {!pending && work.disposition?.note && <p className="arc-hint">{'\u201c'}{work.disposition.note}{'\u201d'}</p>}
      {work.disposition?.by && <p className="arc-hint">Marked by {work.disposition.by}.</p>}
    </div>
  );
}

/**
 * The call's tags (owner, 3 October 2026): the account's five most used as one-press picks, and
 * any words typed -- a reference, a policy number. Saved as they change.
 */
function Tags({ tenantId, callId, work, queryKey }: { tenantId: string; callId: string; work: Work; queryKey: unknown[] }) {
  const qc = useQueryClient();
  const [custom, setCustom] = useState('');
  const set = useMutation({
    mutationFn: (tags: string[]) => api.put(`/v1/tenants/${tenantId}/cdrs/${callId}/tags`, { tags }),
    onSuccess: () => { setCustom(''); void qc.invalidateQueries({ queryKey }); void qc.invalidateQueries({ queryKey: ['cdrs'] }); },
    onError: toastError,
  });
  const on = new Set(work.tags.map((t) => t.toLowerCase()));
  const quick = work.tag_options.filter((t) => !on.has(t.toLowerCase()));
  const add = (t: string): void => {
    const v = t.trim().slice(0, 40);
    if (v && !on.has(v.toLowerCase())) set.mutate([...work.tags, v]);
  };
  return (
    <div className="arc-field" style={{ margin: 0 }}>
      <span className="arc-label">Tags</span>
      <div className="arc-chip-list">
        {work.tags.map((t) => (
          <span key={t} className="arc-chip arc-chip-removable">
            {t}
            {work.may_edit && (
              <button type="button" className="arc-chip-x" aria-label={`Remove ${t}`} disabled={set.isPending}
                onClick={() => set.mutate(work.tags.filter((x) => x !== t))}>
                <Icon name="close" size={11} />
              </button>
            )}
          </span>
        ))}
        {work.may_edit && quick.map((t) => (
          <button key={t} type="button" className="arc-chip arc-chip-toggle" aria-pressed={false} disabled={set.isPending}
            onClick={() => add(t)}>
            <Icon name="plus" size={11} /> {t}
          </button>
        ))}
        {work.tags.length === 0 && !work.may_edit && <span className="arc-subtle" style={{ fontSize: 13 }}>No tags.</span>}
      </div>
      {work.may_edit && (
        <div className="arc-row" style={{ gap: 8, marginTop: 8 }}>
          <input className="arc-input" placeholder="Your own tag: a reference or a policy number" value={custom} maxLength={40}
            aria-label="Your own tag" onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(custom); } }} />
          <button type="button" className="arc-btn arc-btn-sm" disabled={!custom.trim() || set.isPending} onClick={() => add(custom)}>Add</button>
        </div>
      )}
    </div>
  );
}

/** The notes: added, never overwritten; a person may correct their own. */
function Notes({ tenantId, callId, work, queryKey, tz }: { tenantId: string; callId: string; work: Work; queryKey: unknown[]; tz: string }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null);
  const add = useMutation({
    mutationFn: () => api.post(`/v1/tenants/${tenantId}/cdrs/${callId}/notes`, { body: draft.trim() }),
    onSuccess: () => { setDraft(''); void qc.invalidateQueries({ queryKey }); },
    onError: toastError,
  });
  const edit = useMutation({
    mutationFn: (e: { id: string; body: string }) => api.patch(`/v1/tenants/${tenantId}/cdrs/${callId}/notes/${e.id}`, { body: e.body.trim() }),
    onSuccess: () => { setEditing(null); void qc.invalidateQueries({ queryKey }); },
    onError: toastError,
  });
  return (
    <div className="arc-field" style={{ margin: 0 }}>
      <span className="arc-label">Notes</span>
      <div className="arc-stack" style={{ gap: 8 }}>
        {work.notes.map((n) => (
          <div key={n.id} className="arc-cw-note">
            <div className="arc-cw-note-by">
              <b>{n.author}</b><span>{formatDateTime(n.created_at, tz)}{n.edited_at ? ' · edited' : ''}</span>
              <span className="arc-spacer" />
              {n.mine && work.may_edit && editing?.id !== n.id && (
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon arc-btn-sm" aria-label="Correct this note"
                  title="Correct this note" onClick={() => setEditing({ id: n.id, body: n.body })}>
                  <Icon name="edit" size={12} />
                </button>
              )}
            </div>
            {editing?.id === n.id ? (
              <div className="arc-stack" style={{ gap: 6 }}>
                <textarea className="arc-input arc-cw-ta" value={editing.body} maxLength={4000}
                  onChange={(e) => setEditing({ id: n.id, body: e.target.value })} />
                <div className="arc-row" style={{ gap: 6 }}>
                  <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!editing.body.trim() || edit.isPending}
                    onClick={() => edit.mutate(editing)}>Save</button>
                  <button type="button" className="arc-btn arc-btn-sm" onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </div>
            ) : <div className="arc-cw-note-body">{n.body}</div>}
          </div>
        ))}
        {work.notes.length === 0 && !work.may_edit && <p className="arc-subtle" style={{ margin: 0, fontSize: 13 }}>No notes on this call.</p>}
        {work.may_edit && (
          <>
            <textarea className="arc-input arc-cw-ta" placeholder="Add a note. It is kept with the call, with your name and the time."
              value={draft} maxLength={4000} onChange={(e) => setDraft(e.target.value)} />
            <div className="arc-row" style={{ gap: 8, justifyContent: 'space-between' }}>
              <span className="arc-hint" style={{ margin: 0 }}>Notes are added, not overwritten: earlier ones stay as they were.</span>
              <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!draft.trim() || add.isPending}
                onClick={() => add.mutate()}>Add note</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
